import { Component, EventEmitter, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-import-modal',
  templateUrl: './import-modal.component.html',
  styleUrls: ['./import-modal.component.scss']
})
export class ImportModalComponent<T extends Record<string, any>> {
  @ViewChild('modalTpl') modalTpl!: TemplateRef<any>;

  @Input() title: string = 'Import Data';
  @Input() helpText: string = '';
  @Input() columns: { key: string; label: string }[] = [];
  @Input() sampleData: T[] = [];
  @Input() invalidRows: { row: T; errors: string[] }[] = [];

  @Output() importConfirmed = new EventEmitter<T[]>();

  importPreview: T[] = [];
  selectedFile: File | null = null;
  fileSelected = false;
  validationError = '';

  constructor(public modalService: NgbModal) { }

  open() {
    this.reset();

    this.modalService.open(this.modalTpl, {
      centered: true,
      backdrop: 'static',
      windowClass: 'import-modal-custom'
    });
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (!file) return;

    this.selectedFile = file;
    this.fileSelected = true;
    this.validationError = '';

    const reader = new FileReader();
    reader.onload = (e: any) => {
      const csvText = e.target.result;
      const parsed = this.parseCsv(csvText);

      const invalid = parsed.some(row =>
        !this.columns.every(col => col.key in row)
      );

      if (invalid) {
        this.importPreview = [];
        this.validationError =
          'Uploaded file does not match the expected structure. Please check the columns.';
      } else {
        this.importPreview = parsed;
      }
    };
    reader.readAsText(file);
  }

  confirm(modal: NgbModalRef) {
    if (!this.importPreview.length) return;
    this.importConfirmed.emit(this.importPreview);
    modal.close();
  }

  downloadSampleCsv() {
    const rows = this.sampleData && this.sampleData.length
      ? this.sampleData
      : [this.columns.reduce((acc, c) => ({ ...acc, [c.key]: '' }), {} as T)];

    const header = this.columns.map(c => c.key).join(',');
    const csvRows = rows.map(row => this.columns.map(c => row[c.key] ?? '').join(','));
    const csvContent = [header, ...csvRows].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;

    const safeTitle = this.title.replace(/[^a-z0-9]/gi, '_');
    link.setAttribute('download', `Sample_${safeTitle}.csv`);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  private parseCsv(csvText: string): T[] {
    const lines = csvText.split('\n').filter(x => x.trim());
    const headers = lines[0].split(',').map(h => h.trim());

    const result: T[] = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',');
      const obj: any = {};
      headers.forEach((h, index) => (obj[h] = values[index]?.trim() ?? ''));
      result.push(obj);
    }

    return result;
  }

  private reset() {
    this.importPreview = [];
    this.selectedFile = null;
    this.fileSelected = false;
    this.validationError = '';
    this.invalidRows = [];
  }

  getErrorsForRow(row: T): string {
    const invalid = this.invalidRows.find(ir => ir.row === row);
    return invalid?.errors.join(', ') || '';
  }

  isRowInvalid(row: T): boolean {
    return this.invalidRows.some(ir => ir.row === row);
  }

  rowIndex(row: T): number {
    return this.importPreview.indexOf(row) + 1;
  }
}

